import * as PIXI from 'pixi.js';
import {
	game
} from "../Game";
import { Settings } from '../Settings';
import { disableCulling } from '../utils/PIXICuller';
import { gameRandom } from './GameRandom';
import { handOverChunk } from './gore/goreChunks';
import { simulationTime } from './SimulationClock';
const {b2BodyDef, b2FixtureDef, b2CircleShape} = Box2D;

const vec1 = new Box2D.b2Vec2();
const vec2 = new Box2D.b2Vec2();

const poolSize = Settings.maxPhysicsParticles || 96;
const spritePool = [];
const activeParticles = [];
// Copies of resting particles whose bodies went to a new burst.
const leftBehind = [];
const copyPool = [];
let remainingEmissionBudget = Settings.maxPhysicsParticleEmissionsPerFrame;
const FADE_MS = 700;
const MAX_LEFT_BEHIND = 160;
const RESTING_SPEED = 0.5;

const createParticleBody = (sprite, radius) => {
	const bodyDef = new b2BodyDef();
	bodyDef.set_type(Box2D.b2_dynamicBody);
	bodyDef.set_angularDamping(0.9);

	const fixDef = new b2FixtureDef();
	fixDef.set_density(0.1);
	fixDef.set_friction(Settings.defaultFriction);
	fixDef.set_restitution(Settings.defaultRestitution);
	fixDef.get_filter().set_categoryBits(game.editor.MASKBIT_EVERYTHING_BUT_US);
	// Gibs still bounce off terrain and props, but no longer create a contact
	// pair against every limb in the ragdoll that just emitted them.
	fixDef.get_filter().set_maskBits(
		game.editor.MASKBIT_NORMAL | game.editor.MASKBIT_FIXED
	);

	const shape = new b2CircleShape();
	shape.set_m_radius(radius);
	fixDef.set_shape(shape);

	const body = game.editor.CreateBody(bodyDef);
	sprite.particleFixture = body.CreateFixture(fixDef);
	sprite.particleRadius = radius;
	sprite.particleMask = game.editor.MASKBIT_NORMAL | game.editor.MASKBIT_FIXED;
	body.SetEnabled(false);
	body.mySprite = sprite;
	sprite.myBody = body;

	Box2D.destroy(fixDef);
	Box2D.destroy(bodyDef);
	Box2D.destroy(shape);

	return body;
};

const prewarmBodies = () => {
	let remaining = Settings.physicsParticlePrewarmPerFrame || 0;
	const defaultRadius = 5 / Settings.PTM;

	// Warm the entries that will be popped first. Body/fixture construction is
	// spread across quiet frames instead of landing in the collision frame.
	for(let i = spritePool.length - 1; i >= 0 && remaining > 0; i--){
		const sprite = spritePool[i];
		if(sprite.myBody && !sprite.myBody.destroyed) continue;
		createParticleBody(sprite, defaultRadius);
		remaining--;
	}
};

export const init = ()=> {
	remainingEmissionBudget = Settings.maxPhysicsParticleEmissionsPerFrame;
	if(spritePool.length || activeParticles.length) return;
	for(let i = 0; i<poolSize; i++){
		const sprite = new PIXI.Sprite(PIXI.Texture.from('Gore_Meat10000'));
		sprite.isPhysicsParticleSprite = true;
		sprite.visible = false;
		disableCulling(sprite);
		sprite.data = {};
		spritePool.push(sprite);
	}
}

export const beginFrame = () => {
	remainingEmissionBudget = Settings.maxPhysicsParticleEmissionsPerFrame;
	prewarmBodies();
};

const resting = body => {
	const velocity = body.GetLinearVelocity();
	const x = velocity.get_x();
	const y = velocity.get_y();
	if (x * x + y * y > RESTING_SPEED * RESTING_SPEED) return false;
	for (let edge = body.GetContactList(); Box2D.getPointer(edge) !== Box2D.getPointer(Box2D.NULL); edge = edge.get_next()) {
		if (edge.get_contact().IsTouching() && edge.get_other().GetType() !== Box2D.b2_staticBody) return false;
	}
	return true;
};

// A particle lying on the ground leaves a copy without a body when the pool needs
// its body, so the copy stays until it would have faded. Visual only.
const leaveCopy = sprite => {
	if (!sprite.parent || !resting(sprite.myBody)) return;
	let copy = leftBehind.length >= MAX_LEFT_BEHIND ? leftBehind.shift() : copyPool.pop();
	if (!copy || copy.destroyed) {
		copy = new PIXI.Sprite();
		copy.isPhysicsParticleSprite = true;
		copy.data = {};
		disableCulling(copy);
	}
	copy.texture = sprite.texture;
	copy.position.copyFrom(sprite.position);
	copy.pivot.copyFrom(sprite.pivot);
	copy.scale.copyFrom(sprite.scale);
	copy.rotation = sprite.rotation;
	copy.tint = sprite.tint;
	copy.alpha = sprite.alpha;
	copy.renderable = sprite.renderable;
	copy.visible = true;
	copy.lifeTime = sprite.lifeTime;
	copy.lifeDate = sprite.lifeDate;
	if (copy.parent) copy.parent.removeChild(copy);
	sprite.parent.addChildAt(copy, sprite.parent.getChildIndex(sprite));
	handOverChunk(sprite, copy);
	leftBehind.push(copy);
};

// Food/packaging fragments are cosmetic: terrain bounces need the solver, but
// contacts with every unbroken prop in a pile add no useful gameplay.
export const emitDebris = (textures, worldPosition, amount, size, force, randomTexture = true, tints=[], rotation=0, offset=[0,0]) =>
	emit(textures, worldPosition, amount, size, force, randomTexture, tints, rotation, offset, undefined, -1, true, true);

export const emit = (textures, worldPosition, amount, size, force, randomTexture = true, tints=[], rotation=0, offset=[0,0], baseVelocity={x:0, y:0}, spriteIndex=-1, addTextureZeroes=true, cosmeticDebris=false) => {
	const emissionCount = Math.min(Math.max(0, amount), remainingEmissionBudget);
	remainingEmissionBudget -= emissionCount;
	const used = [];
	if(emissionCount === 0) return used;

	let force2 = force*2;
	size = size / Settings.PTM;
	for(let i = 0; i<emissionCount; i++){
		const impulse = vec1;

		let sprite, body;
		if(spritePool.length>0){
			sprite = spritePool.pop();
			body = sprite.myBody;
			if(!body || body.destroyed){
				body = createParticleBody(sprite, size);
			}

			if(spriteIndex >= 0){
				game.editor.textures.addChildAt(sprite, spriteIndex);
			}else{
				game.editor.textures.addChild(sprite);
			}
			sprite.visible = true;
			disableCulling(sprite);
			activeParticles.push(sprite);
		}else{
			// Recycle the oldest particle. The frame budget never exceeds the
			// pool size, so a burst cannot take back what it just threw.
			sprite = activeParticles.shift();
			activeParticles.push(sprite);
			leaveCopy(sprite);
			sprite.renderable = true;
			body = sprite.myBody;
		}
		const mask = cosmeticDebris ? game.editor.MASKBIT_FIXED : game.editor.MASKBIT_NORMAL | game.editor.MASKBIT_FIXED;
		if(sprite.particleRadius !== size || sprite.particleMask !== mask){
			// Rebuild broadphase proxies after changing the shape, and finish old
			// contacts using the previous particle's flags before recycling it.
			if(body.IsEnabled()) body.SetEnabled(false);
			if(sprite.particleRadius !== size){
				sprite.particleFixture.GetShape().set_m_radius(size);
				body.ResetMassData();
				sprite.particleRadius = size;
			}
			if(sprite.particleMask !== mask){
				const filter = sprite.particleFixture.GetFilterData();
				filter.set_maskBits(mask);
				sprite.particleFixture.SetFilterData(filter);
				sprite.particleMask = mask;
			}
		}
		sprite.isCosmeticDebris = cosmeticDebris;
		if(!randomTexture){
			const texture = textures[i%textures.length];
			sprite.texture = typeof texture === 'string' ? PIXI.Texture.from(texture + (addTextureZeroes ? '0000' : '')) : texture;
		}else{
			const randomTexture = textures[Math.floor(Math.random()*textures.length)];
			sprite.texture = PIXI.Texture.from(randomTexture+'0000');
		}

		sprite.lifeTime = Settings.physicsParticleLifeTime + Math.round(Settings.physicsParticleLifeTimeRandomOffset*gameRandom());
		sprite.lifeDate = simulationTime();
		sprite.alpha = 1;

		sprite.pivot.x = sprite.width/2;
		sprite.pivot.y = sprite.height/2;

		const targetPosition = vec2;
		targetPosition.Set(worldPosition.x, worldPosition.y);

		if(offset){
			if(offset[0] !== 0){

				const offsetLength = (offset[0] * 2) * gameRandom() - offset[0];
				const offsetX = offsetLength * Math.cos(rotation);
				const offsetY = offsetLength * Math.sin(rotation);

				targetPosition.set_x(targetPosition.get_x() + offsetX);
				targetPosition.set_y(targetPosition.get_y() + offsetY);

			}

			if(offset[1] !== 0){

				const offsetLength = (offset[1] * 2) * gameRandom() - offset[1];
				const offsetX = offsetLength * Math.cos(rotation+Settings.pihalve);
				const offsetY = offsetLength * Math.sin(rotation+Settings.pihalve);

				targetPosition.set_x(targetPosition.get_x() + offsetX);
				targetPosition.set_y(targetPosition.get_y() + offsetY);

			}
		}


		body.SetTransform(targetPosition, body.GetAngle());

		sprite.x = targetPosition.get_x()*Settings.PTM;
		sprite.y = targetPosition.get_y()*Settings.PTM;

		impulse.Set(gameRandom()*force2-force + baseVelocity.x, gameRandom()*force2-force + baseVelocity.y)
		body.SetLinearVelocity(impulse);
		body.SetAngularVelocity((gameRandom() * 2 - 1) * 8);
		if(!body.IsEnabled()) body.SetEnabled(true);

		if(tints.length>0){
			sprite.tint = tints[Math.floor(Math.random()*tints.length)];
		}else{
			sprite.tint = 0xFFFFFF;
		}
		used.push(sprite);
	}
	return used;
}

export const update = clean => {
	if(clean){
		for(let i = activeParticles.length - 1; i >= 0; i--){
			const sprite = activeParticles[i];
			sprite.visible = false;
			if(sprite.parent) sprite.parent.removeChild(sprite);
			if(!spritePool.includes(sprite)) spritePool.push(sprite);
		}
		activeParticles.length = 0;
		for(const copy of leftBehind){
			if(copy.destroyed) continue;
			copy.visible = false;
			if(copy.parent) copy.parent.removeChild(copy);
			copyPool.push(copy);
		}
		leftBehind.length = 0;

		// resetEditor has just destroyed every Box2D body. Do not retain stale
		// WASM wrappers in the visual pool; the next level prewarms fresh ones.
		for(const sprite of spritePool){
			if(sprite.myBody && sprite.myBody.mySprite === sprite){
				delete sprite.myBody.mySprite;
			}
			delete sprite.myBody;
			delete sprite.particleFixture;
			delete sprite.particleRadius;
			delete sprite.particleMask;
			sprite.isCosmeticDebris = false;
		}
		remainingEmissionBudget = Settings.maxPhysicsParticleEmissionsPerFrame;
		return;
	}

	const now = simulationTime();
	for(let i = 0; i<activeParticles.length; i++){
		const sprite = activeParticles[i];
		sprite.alpha = Math.min(1, (sprite.lifeTime - (now - sprite.lifeDate)) / FADE_MS);
		if(now - sprite.lifeDate > sprite.lifeTime){
			const body = sprite.myBody;
			body.SetEnabled(false);
			vec1.Set(0, 0);
			body.SetLinearVelocity(vec1);
			body.SetAngularVelocity(0);

			if(sprite.parent) sprite.parent.removeChild(sprite);
			sprite.visible = false;
			activeParticles.splice(i, 1);
			spritePool.push(sprite);

			i--;
		}
	}
	for(let i = 0; i<leftBehind.length; i++){
		const copy = leftBehind[i];
		const left = copy.lifeTime - (now - copy.lifeDate);
		if(!copy.destroyed && left >= 0){
			copy.alpha = Math.min(1, left / FADE_MS);
			continue;
		}
		if(!copy.destroyed){
			copy.visible = false;
			if(copy.parent) copy.parent.removeChild(copy);
			copyPool.push(copy);
		}
		leftBehind.splice(i, 1);
		i--;
	}
}
